import React, { useState } from "react";
import { IoCloseOutline, IoSparklesOutline, IoSendOutline } from "react-icons/io5";
import { useSubmitOnboarding } from "../services/OnboardingService";
import type { OnboardingResponse } from "../../../../shared/models/Onboarding";
import "./OnboardingModal.scss";

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: (response: OnboardingResponse) => void;
}

/**
 * Onboarding-Modal fuer das KI-gestuetzte Onboarding.
 * Wird nach dem ersten Login angezeigt, um den User nach seinem Unternehmen zu fragen.
 */
export default function OnboardingModal({
  isOpen,
  onClose,
  onCompleted,
}: OnboardingModalProps) {
  const [freeText, setFreeText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardingResponse | null>(null);

  const submitMutation = useSubmitOnboarding();

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validierung
    const trimmed = freeText.trim();
    if (trimmed.length < 20) {
      setError("Bitte gib mindestens 20 Zeichen ein, damit die KI dein Unternehmen verstehen kann.");
      return;
    }
    if (trimmed.length > 2000) {
      setError("Bitte maximal 2000 Zeichen eingeben.");
      return;
    }

    submitMutation.mutate(trimmed, {
      onSuccess: (response) => {
        console.log("[OnboardingModal] AI response:", response);
        setResult(response);
        if (onCompleted) {
          onCompleted(response);
        }
      },
      onError: (err) => {
        console.error("[OnboardingModal] Fehler beim Onboarding:", err);
        const axiosError = err as {
          response?: {
            data?: {
              detail?: string;
              message?: string;
              title?: string;
            };
          };
        };
        let message = "Die KI-Analyse ist fehlgeschlagen. Bitte erneut versuchen.";
        if (axiosError?.response?.data?.detail) {
          message = axiosError.response.data.detail;
        } else if (axiosError?.response?.data?.message) {
          message = axiosError.response.data.message;
        } else if (axiosError?.response?.data?.title) {
          message = axiosError.response.data.title;
        } else if (err.message) {
          message = err.message;
        }
        setError(message);
      },
    });
  };

  const handleSkip = () => {
    onClose();
  };

  const handleClose = () => {
    setFreeText("");
    setError(null);
    setResult(null);
    submitMutation.reset();
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Ergebnis-Ansicht nach erfolgreicher KI-Analyse
  if (result && result.onboardingCompleted) {
    return (
      <div className="onboarding-overlay" onClick={handleOverlayClick}>
        <div className="onboarding-modal" onClick={(e) => e.stopPropagation()}>
          <button
            className="onboarding-modal__close"
            onClick={handleClose}
            aria-label="Schliessen"
          >
            <IoCloseOutline />
          </button>

          <div className="onboarding-modal__content">
            <div className="onboarding-modal__icon">
              <IoSparklesOutline />
            </div>

            <h2 className="onboarding-modal__title">
              Perfekt, wir haben dein Unternehmen verstanden!
            </h2>

            <p className="onboarding-modal__subtitle">
              Basierend auf deiner Beschreibung haben wir folgende Informationen erkannt:
            </p>

            <div className="onboarding-modal__result-card">
              <div className="onboarding-modal__result-row">
                <span className="onboarding-modal__result-label">Branche:</span>
                <span className="onboarding-modal__result-value">{result.industry}</span>
              </div>
              <div className="onboarding-modal__result-row">
                <span className="onboarding-modal__result-label">Zielgruppe:</span>
                <span className="onboarding-modal__result-value">{result.targetAudience}</span>
              </div>
              <div className="onboarding-modal__result-row">
                <span className="onboarding-modal__result-label">Geschaeftsmodell:</span>
                <span className="onboarding-modal__result-value">{result.businessModel}</span>
              </div>
              <div className="onboarding-modal__result-row">
                <span className="onboarding-modal__result-label">Leistungsversprechen:</span>
                <span className="onboarding-modal__result-value">{result.valueProposition}</span>
              </div>
              {result.description && (
                <div className="onboarding-modal__result-row">
                  <span className="onboarding-modal__result-label">Beschreibung:</span>
                  <span className="onboarding-modal__result-value">{result.description}</span>
                </div>
              )}
            </div>

            <p className="onboarding-modal__hint">
              Deine Module werden jetzt personalisiert angezeigt.
            </p>

            <button
              type="button"
              className="onboarding-modal__button onboarding-modal__button--primary"
              onClick={handleClose}
            >
              Los geht's!
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Eingabe-Ansicht
  return (
    <div className="onboarding-overlay" onClick={handleOverlayClick}>
      <div className="onboarding-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="onboarding-modal__close"
          onClick={handleClose}
          aria-label="Schliessen"
        >
          <IoCloseOutline />
        </button>

        <div className="onboarding-modal__content">
          <div className="onboarding-modal__icon">
            <IoSparklesOutline />
          </div>

          <h2 className="onboarding-modal__title">
            👋 Hallo!
          </h2>

          <p className="onboarding-modal__subtitle">
            Ich bin dein Onboarding Assistant - ich helfe dir dabei, deinen Arbeitsbereich
            an dein Unternehmen anzupassen, damit du sofort loslegen kannst.
          </p>

          <p className="onboarding-modal__description">
            <strong>Erzaehl uns kurz:</strong> Was macht dein Unternehmen und wer sind deine Kunden?
          </p>

          <form className="onboarding-modal__form" onSubmit={handleSubmit}>
            <textarea
              className="onboarding-modal__textarea"
              placeholder="Beispiel: Wir sind ein Handyshop in Muenchen und verkaufen Smartphones mit Laufzeitvertraegen an Privatkunden. Unser Fokus liegt auf Beratung und schneller Vertragsabwicklung..."
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              disabled={submitMutation.isPending}
              rows={6}
              maxLength={2000}
            />

            <div className="onboarding-modal__char-count">
              {freeText.length} / 2000 Zeichen
            </div>

            {error && <p className="onboarding-modal__error">{error}</p>}

            <div className="onboarding-modal__actions">
              <button
                type="button"
                className="onboarding-modal__button onboarding-modal__button--secondary"
                onClick={handleSkip}
                disabled={submitMutation.isPending}
              >
                Ueberspringen
              </button>

              <button
                type="submit"
                className="onboarding-modal__button onboarding-modal__button--primary"
                disabled={submitMutation.isPending || freeText.trim().length < 20}
              >
                {submitMutation.isPending ? (
                  <>
                    <span className="onboarding-modal__spinner" />
                    KI analysiert...
                  </>
                ) : (
                  <>
                    <IoSendOutline />
                    Absenden
                  </>
                )}
              </button>
            </div>
          </form>

          <p className="onboarding-modal__footer">
            KI-Antworten sind moeglicherweise nicht immer praezise.
          </p>
        </div>
      </div>
    </div>
  );
}